import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CommunityService, CommentService } from '../services/communityService';
import type { CommunityPost, Comment } from '../types/community';
import { getCommunityPostUrl } from '../config/site';

const CommunityPostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [post, setPost] = useState<CommunityPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deletingCommentIds, setDeletingCommentIds] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const postData = await CommunityService.getPostById(id);
        if (!isMounted) return;
        setPost(postData);

        // Load comments for this post
        setCommentsLoading(true);
        const commentsData = await CommentService.listComments({
          communityId: id,
          limit: 100,
        });
        if (!isMounted) return;
        setComments(commentsData.items || []);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load post details');
      } finally {
        if (!isMounted) return;
        setLoading(false);
        setCommentsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const formatDate = (iso: string | undefined | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN');
  };

  const formatDateValue = (value: unknown) => {
    if (!value) return '—';
    if (typeof value === 'string') return formatDate(value);
    if (typeof value === 'object' && value && '$date' in (value as any) && typeof (value as any).$date === 'string') {
      return formatDate((value as any).$date);
    }
    return '—';
  };

  const formatAmount = (value: unknown) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const pickFirstString = (obj: any, keys: string[]) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };

  const normalizeToStringArray = (value: unknown): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter((v) => typeof v === 'string' && v.trim()).map((v) => v.trim());
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    return [];
  };

  const getCardLabel = (postObj: any): string => {
    const v = postObj?.cardId ?? postObj?.card;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name.trim() : '';
      const id = typeof v._id === 'string' ? v._id.trim() : '';
      if (name && id) return `${name} (${id})`;
      if (name) return name;
      if (id) return id;
    }
    return '—';
  };

  const normalizeToArray = (value: unknown): any[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return [parsed];
      } catch {
        return [raw];
      }
    }
    if (typeof value === 'object') {
      const obj: any = value;
      if (Array.isArray(obj.items)) return obj.items;
      if (Array.isArray(obj.data)) return obj.data;
      return [obj];
    }
    return [value];
  };

  const answerToLabel = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value !== 'object') return '';

    const obj: any = value;
    const question = pickFirstString(obj, ['question', 'q', 'prompt', 'label', 'title', 'name']);
    const answer = pickFirstString(obj, ['answer', 'a', 'value', 'response', 'text', 'selected', 'option']);
    const id = pickFirstString(obj, ['_id', 'id']);

    if (question && answer) return `${question}: ${answer}`;
    if (question) return question;
    if (answer) return answer;
    if (id) return id;

    try {
      return JSON.stringify(obj);
    } catch {
      return '';
    }
  };

  const getUserLabel = (obj: any): string => {
    const v = obj?.userId ?? obj?.user;
    if (!v) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') {
      const name = typeof v.name === 'string' ? v.name.trim() : '';
      const email = typeof v.email === 'string' ? v.email.trim() : '';
      if (name && email) return `${name} (${email})`;
      if (name) return name;
      if (email) return email;
    }
    return '—';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading post details...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
        <p className="text-gray-500 mb-4">{error || 'Post not found'}</p>
        <button
          onClick={() => navigate('/community')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Posts
        </button>
      </div>
    );
  }

  const hardDeleteComment = async (commentId: string) => {
    const ok = window.confirm('Hard delete this comment? This cannot be undone.');
    if (!ok) return;

    try {
      setDeletingCommentIds((prev) => ({ ...prev, [commentId]: true }));
      setError(null);
      await CommentService.hardDeleteComment(commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to hard delete comment');
    } finally {
      setDeletingCommentIds((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
    }
  };

  const description = pickFirstString(post, ['description', 'desc', 'body', 'text', 'caption']);
  const links = normalizeToStringArray((post as any).url || (post as any).link || (post as any).links);
  const images = normalizeToStringArray((post as any).images).length
    ? normalizeToStringArray((post as any).images)
    : normalizeToStringArray((post as any).image);
  const tags = normalizeToStringArray((post as any).tags);
  const category = pickFirstString(post, ['category', 'categoryName', 'topic']);
  const answersArray = normalizeToArray(
    (post as any).answers ??
    (post as any).answer ??
    (post as any).responses ??
    (post as any).questionAnswers
  );

  return (
    <div className="space-y-6">
      {/* Post Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{post.title || 'Untitled Post'}</h1>
          <p className="mt-1 text-sm text-gray-500">ID: {post._id}</p>
        </div>
        <div className="flex items-center space-x-2">
          <a
            href={getCommunityPostUrl(post)}
            target="_blank"
            rel="noreferrer"
            title="View on website"
            aria-label="View on website"
            className="inline-flex items-center justify-center h-10 w-10 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </a>
          <button
            type="button"
            disabled={deleting}
            onClick={async () => {
              if (!id) return;
              const ok = window.confirm('Hard delete this community post? This cannot be undone.');
              if (!ok) return;

              try {
                setDeleting(true);
                setError(null);
                await CommunityService.hardDeletePost(id);
                navigate('/community');
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to hard delete post');
              } finally {
                setDeleting(false);
              }
            }}
            title="Hard delete"
            aria-label="Hard delete"
            className={
              `inline-flex items-center justify-center h-10 w-10 border rounded-md bg-white ` +
              (deleting
                ? 'border-red-200 text-red-300 cursor-not-allowed'
                : 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400')
            }
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
          <button
            onClick={() => navigate('/community')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back to Posts
          </button>
        </div>
      </div>

      {/* Post Details Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 space-y-6">
          {/* Title & Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Title</h3>
              <p className="mt-2 text-lg text-gray-900">{post.title || '—'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Amount</h3>
              <p className="mt-2 text-lg text-gray-900">{formatAmount(post.amount)}</p>
            </div>
          </div>

          {/* Where & User */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Where</h3>
              <p className="mt-2 text-gray-900">{post.where || '—'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">User</h3>
              <p className="mt-2 text-gray-900">{getUserLabel(post)}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Description</h3>
            <p className="mt-2 text-gray-900 whitespace-pre-wrap">{description || '—'}</p>
          </div>

          {/* Card / Category / Tags */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Card</h3>
              <p className="mt-2 text-gray-900">{getCardLabel(post)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Category</h3>
              <p className="mt-2 text-gray-900">{category || '—'}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Tags</h3>
              {tags.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <span key={t} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {t}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-gray-900">—</p>
              )}
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Links</h3>
            {links.length ? (
              <div className="mt-2 space-y-1">
                {links.map((href) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:text-blue-700 break-all text-sm"
                  >
                    {href}
                  </a>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-gray-900">—</p>
            )}
          </div>

          {/* Image */}
          {images.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Image</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {images.map((src) => (
                  <img key={src} src={src} alt={post.title} className="max-h-64 rounded-md border border-gray-200" />
                ))}
              </div>
            </div>
          )}

          {/* Answers */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Answers</h3>
            {answersArray.length ? (
              <div className="mt-2">
                <p className="text-sm text-gray-700">{answersArray.length} item(s)</p>

                <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-gray-900">
                  {answersArray
                    .map((a) => answerToLabel(a))
                    .filter((s) => Boolean(s))
                    .map((label, idx) => (
                      <li key={`${idx}-${label.slice(0, 24)}`}>{label}</li>
                    ))}
                </ul>

                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-700">View answers JSON</summary>
                  <pre className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md overflow-auto text-xs text-gray-800">
                    {JSON.stringify(answersArray, null, 2)}
                  </pre>
                </details>
              </div>
            ) : (
              <p className="mt-2 text-gray-900">—</p>
            )}
          </div>

          {/* Status & Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-200">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Status</h3>
              <div className="mt-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  post.status === 'approved' ? 'bg-green-100 text-green-800' :
                  post.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {post.status || 'pending'}
                </span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Created</h3>
              <p className="mt-2 text-gray-900">{formatDateValue(post.createdAt)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Updated</h3>
              <p className="mt-2 text-gray-900">{formatDateValue(post.updatedAt)}</p>
            </div>
          </div>

          {/* Deleted / Raw */}
          <div className="pt-6 border-t border-gray-200 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Deleted</h3>
                <p className="mt-2 text-gray-900">{formatDateValue((post as any).deletedAt)}</p>
              </div>
            </div>

            <details>
              <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-700">More info (raw post JSON)</summary>
              <pre className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md overflow-auto text-xs text-gray-800">
                {JSON.stringify(post, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Comments ({comments.length})
        </h2>

        {commentsLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="mt-2 text-gray-500">Loading comments...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No comments yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment._id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <p className="font-medium text-gray-900">{getUserLabel(comment)}</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        comment.status === 'approved' ? 'bg-green-100 text-green-800' :
                        comment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {comment.status || 'pending'}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      {formatDateValue(comment.createdAt)}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={Boolean(deletingCommentIds[comment._id])}
                    onClick={() => hardDeleteComment(comment._id)}
                    title="Hard delete comment"
                    aria-label="Hard delete comment"
                    className={
                      `ml-4 inline-flex items-center justify-center h-9 w-9 border rounded-md bg-white ` +
                      (deletingCommentIds[comment._id]
                        ? 'border-red-200 text-red-300 cursor-not-allowed'
                        : 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400')
                    }
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>

                <p className="mt-3 text-gray-900">{comment.description || '—'}</p>

                <div className="mt-3 text-xs text-gray-500">
                  ID: {comment._id}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPostDetail;
